import { Card, DatePicker, Input, ProgressBar } from '@design-systems/canvas-ui'
import React, { useEffect, useState } from 'react'
import { format, parse, isValid } from 'date-fns'
import { MPMLayout } from 'components/layout'
import { clearLocationParam } from 'utils/helpers'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert, ErrorFallback } from 'components/error-fallback'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useNotes } from 'hooks/notes/useNotes'
import { parseStoreMetrics } from '../../pages/notes/utils/parseStoreMetrics'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { ErrorBoundary } from 'react-error-boundary'
import { NoteItem } from './note-types'
import { NotesItems } from './notes-items'
import { validateNotesPermissions } from 'utils/notesPermissions'

import './notes.css'
import './notes.scss'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { addNoteItem, updateNoteItem } from 'queries/notes/notes'
import { MethodConstants } from 'constants/constants'
import { QueryKeys } from 'constants/query-keys'
import { getLegacyNotes } from './helpers'
import { StoreMetrics } from './types/storeMetrics'

export const NotesPage = () => {
    const { t, params, setParams, currentLocation } = useEssentials()
    const { notes } = TRANSLATION_KEYS

    window.onpopstate = () => {
        clearLocationParam({ params, setParams })
    }

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t('notes')}
                />
            )}
        >
            <MPMLayout
                heading={t(notes.notes)}
                breadcrumbsEnabled={false}
                hideFeedback={true}
            >
                <Notes key={currentLocation} />
            </MPMLayout>
        </ErrorBoundary>
    )
}

const Notes = () => {
    const {
        t,
        currentLocation,
        env,
        session,
        greenfieldGroups,
        userLocationHierarchy,
    } = useEssentials()

    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date())
    const formattedDate = formatSelectedDate(selectedDate)
    const { notes: notesKeys } = TRANSLATION_KEYS
    const { items } = notesKeys
    const { todo, attendance, notes: notesItems } = items

    const permissionCheck = validateNotesPermissions(
        greenfieldGroups,
        currentLocation,
        userLocationHierarchy?.store_id,
        t,
    )

    const { data, isLoading, isError, error, refetch } = useNotes({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        date: formattedDate,
        enabled: permissionCheck.canRead,
    })

    const [notes, setNotes] = useState<NoteItem[]>([])
    const queryClient = useQueryClient()

    useEffect(() => {
        if (data) {
            setNotes(data.notes_items)
        }
    }, [data])

    const noteItemAddMutation = useMutation({
        mutationFn: (body: NoteItem) => {
            return addNoteItem({
                env,
                accessToken: session?.accessToken ?? '',
                filters: {
                    location_id: currentLocation.slice(1),
                    is_past_date: 'false',
                    date: formattedDate,
                },
                method: MethodConstants.POST,
                body,
            })
        },
        onSuccess: () =>
            queryClient.invalidateQueries([
                QueryKeys.NOTES,
                currentLocation,
                formattedDate,
            ]),
    })

    const noteItemUpdateMutation = useMutation({
        mutationFn: (body: NoteItem) => {
            return updateNoteItem({
                env,
                accessToken: session?.accessToken ?? '',
                filters: {
                    location_id: currentLocation.slice(1),
                    is_past_date: 'false',
                    date: formattedDate,
                },
                dynamicSubPath: '/' + body.id,
                method: MethodConstants.PUT,
                body,
            })
        },
        onSuccess: () =>
            queryClient.invalidateQueries([
                QueryKeys.NOTES,
                currentLocation,
                formattedDate,
            ]),
    })

    if (!permissionCheck.canRead) {
        return (
            <ErrorAlert
                error={
                    new Error(
                        permissionCheck.reason ||
                            t(notesKeys.permissions.noAccess),
                    )
                }
                variant="custom"
            />
        )
    }

    if (isLoading) {
        return (
            <div data-testid="placeholder">
                <BasicPlaceholder type="text" rows={4} />
            </div>
        )
    }
    if (isError || !data) {
        return <ErrorAlert error={error} onRetry={() => refetch()} />
    }

    const metrics = parseStoreMetrics(data)

    const isMutationInProgress =
        noteItemAddMutation.isLoading || noteItemUpdateMutation.isLoading

    const legacyNotes = data.legacy_notes[0].notes
    const legacyNotesExist = legacyNotes.length > 0

    return (
        <div>
            <NotesDatePicker
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
            />
            <ProgressBars metrics={metrics} />
            <Weather metrics={metrics} />
            <KeyStatistics metrics={metrics} />
            <div>
                {[todo, attendance, notesItems].map((item, index) => (
                    <NotesItems
                        title={t(item.title)}
                        addLabel={t(item.label)}
                        notes={notes}
                        setNotes={setNotes}
                        handleAdd={noteItemAddMutation.mutate}
                        handleUpdate={noteItemUpdateMutation.mutate}
                        formattedDate={formattedDate}
                        isInProgress={isMutationInProgress}
                        type={t(item.key)}
                        key={t(item.key) + JSON.stringify(notes)}
                        isCheckboxEnabled={index === 0}
                        hasWritePermission={permissionCheck.canWrite}
                    />
                ))}
            </div>
            {legacyNotesExist && (
                <div className="legacy-notes">
                    <h3 className="hc8-mt-6x">
                        {t(notesKeys.legacy.title)}{' '}
                        <small>{t(notesKeys.legacy.subtitle)}</small>
                    </h3>
                    <Input.Textarea
                        id="legacy_notes"
                        value={getLegacyNotes([legacyNotes[0]])}
                        isDisabled
                        canGrow
                    />
                </div>
            )}
        </div>
    )
}

const formatSelectedDate = (selectedDate: Date | null) => {
    return selectedDate
        ? format(selectedDate, 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd')
}

const DATE_PICKER_FORMAT = 'MM/dd/yyyy' as const

const NotesDatePicker = ({
    selectedDate,
    setSelectedDate,
}: {
    selectedDate: Date | null
    setSelectedDate: (date: Date | null) => void
}) => {
    const { t } = useEssentials()
    const { notes } = TRANSLATION_KEYS

    const handleDateChange = (
        _id: string,
        value: string | { startDate: string; endDate: string } | null,
    ) => {
        if (typeof value !== 'string' || value === '') {
            setSelectedDate(null)
            return
        }
        const parsedDate = parse(value, DATE_PICKER_FORMAT, new Date())
        setSelectedDate(isValid(parsedDate) ? parsedDate : null)
    }

    return (
        <div>
            <DatePicker
                id="notes-date-picker"
                data-testid="notes-date-picker"
                aria-label={t(notes.datePickerLabel)}
                format={DATE_PICKER_FORMAT}
                onChange={handleDateChange}
                value={
                    selectedDate ? format(selectedDate, DATE_PICKER_FORMAT) : ''
                }
                errorText={t(notes.errorText)}
            />
        </div>
    )
}

const ProgressBars = ({ metrics }: { metrics: StoreMetrics }) => {
    const { t } = useEssentials()
    const { notes } = TRANSLATION_KEYS

    return (
        <>
            <p className="progress-bar-row">
                <span>{t(notes.sales)}</span>
                <span>{`$${metrics.salesValue.toLocaleString()} ${t(notes.of)} $${metrics.forecastValue.toLocaleString()}`}</span>
            </p>
            <ProgressBar
                percentComplete={Math.min(metrics.salesProgress, 100)}
            />
            <p>
                <span>
                    {t(notes.comp)}
                    {': '}
                    <span className={metrics.compPercStyle}>
                        {metrics.formattedCompPerc}
                    </span>
                </span>
            </p>
            <p className="progress-bar-row">
                <span>{t(notes.payroll)}</span>
                <span>{`${metrics.payrollUsed} ${t(notes.of)} ${metrics.payrollPlanned} ${t(notes.hours)}`}</span>
            </p>
            <ProgressBar
                percentComplete={Math.min(metrics.payrollProgress, 100)}
            />
            <p>
                <span className={metrics.flexPlanStyle}>
                    {metrics.formattedFlexPlan}
                </span>
                {' ' + t(notes.flexPlan)}
            </p>
        </>
    )
}

const Weather = ({ metrics }: { metrics: StoreMetrics }) => {
    const { t } = useEssentials()
    const { notes } = TRANSLATION_KEYS

    return (
        <div>
            <h3 style={{ marginBottom: '0px' }}>{t(notes.weather)}</h3>
            <Card>
                <div className="weather-section">
                    <div className="weather-content">
                        {metrics.weatherIconUrl && (
                            <img
                                src={metrics.weatherIconUrl}
                                alt=""
                                className="weather-icon"
                            />
                        )}
                        <div>
                            <p className="hc8-mb-none">
                                {`${t(notes.high)}:`} {`${metrics.maxTemp}° F`}
                            </p>
                            <p className="hc8-mb-none">
                                {`${t(notes.low)}:`} {`${metrics.minTemp}° F`}
                            </p>
                            <p className="hc8-mb-none">
                                {metrics.weatherDescription.toLocaleLowerCase()}
                            </p>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    )
}

const KeyStatistics = ({ metrics }: { metrics: StoreMetrics }) => {
    const { t } = useEssentials()
    const { notes } = TRANSLATION_KEYS
    const getHasValue = (value: number): number | string => {
        if (value > 0) {
            return value
        }

        return '-'
    }
    const noValueDisplay = '-'

    return (
        <div>
            <h3 className="key-statistics-heading">{t(notes.keyStatistics)}</h3>
            <Card>
                <div className="key-statistics">
                    <div className="key-statistics-grid">
                        {[
                            [
                                t(notes.sfsInf),
                                getHasValue(metrics.sfsInf),
                                metrics.sfsInfColor,
                                t(metrics.sfsStatus),
                            ],
                            [
                                t(notes.opuInf),
                                getHasValue(metrics.opuInf),
                                metrics.opuInfColor,
                                t(metrics.opuStatus),
                            ],
                            [
                                t(notes.pickOnTime),
                                metrics.pickOnTime,
                                metrics.pickOnTimeColor,
                                t(metrics.pickOnTimeStatus),
                            ],
                        ].map(([label, value, color, status]) => (
                            <React.Fragment key={label}>
                                <span className="hc8-mb-none">{label}</span>
                                <span className="hc8-ta-right">
                                    {value === noValueDisplay
                                        ? value
                                        : `${value}%`}
                                </span>
                                <span
                                    className={`${value === noValueDisplay ? '' : color} hc8-ta-right`}
                                >
                                    {value === noValueDisplay
                                        ? noValueDisplay
                                        : status}
                                </span>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </Card>
        </div>
    )
}

export default NotesPage
